import { FC } from 'react';
import Box from '@mui/material/Box';
import { closestCenter, DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import PhDotsSixSvg from '../../../assets/svg-fc/PhDotsSixSvg';
import PhPushPinSvg from '../../../assets/svg-fc/PhPushPinSvg';
import { UI_TOKENS } from '../../../constants/designTokens';
import { orderColumnKeys } from '../draggable-columns/DraggableColumns';

export interface iColumnsMenuProps {
  columns: { key: string; label: string }[];
  hiddenColumns: string[];
  onToggleColumn: (key: string) => void;
  columnOrder?: string[];
  onColumnOrderChange?: (order: string[]) => void;
  pinnedColumns?: string[];
  onTogglePin?: (key: string) => void;
}

const Toggle = ({ on }: { on: boolean }) => (
  <Box
    component="span"
    sx={{
      position: 'relative',
      width: 28,
      height: 16,
      flexShrink: 0,
      borderRadius: 999,
      bgcolor: on ? UI_TOKENS.sage600 : UI_TOKENS.grey400,
      transition: 'background-color 150ms ease',
      '&::after': {
        content: '""',
        position: 'absolute',
        top: 2,
        left: on ? 14 : 2,
        width: 12,
        height: 12,
        borderRadius: '50%',
        bgcolor: '#fff',
        transition: 'left 150ms ease',
      },
    }}
  />
);

interface iColumnRowProps {
  column: { key: string; label: string };
  shown: boolean;
  locked: boolean;
  pinned: boolean;
  draggable: boolean;
  onToggle: () => void;
  onTogglePin?: () => void;
}

const ColumnRow: FC<iColumnRowProps> = ({ column, shown, locked, pinned, draggable, onToggle, onTogglePin }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: column.key,
    disabled: !draggable,
  });
  return (
    <Box
      ref={setNodeRef}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        height: 34,
        padding: '0 8px 0 4px',
        borderRadius: '6px',
        bgcolor: isDragging ? UI_TOKENS.grey50 : 'transparent',
        transform: CSS.Translate.toString(transform),
        transition,
        position: 'relative',
        zIndex: isDragging ? 1 : 'auto',
        '&:hover': { bgcolor: UI_TOKENS.grey50 },
      }}
    >
      <Box
        component="span"
        {...attributes}
        {...listeners}
        aria-label={`Move ${column.label}`}
        sx={{
          display: 'inline-flex',
          color: UI_TOKENS.textMuted,
          cursor: draggable ? 'grab' : 'default',
          visibility: draggable ? 'visible' : 'hidden',
          touchAction: 'none',
        }}
      >
        <PhDotsSixSvg size={14} />
      </Box>
      <Box component="span" sx={{ flex: 1, fontSize: 13, color: UI_TOKENS.textStrong, whiteSpace: 'nowrap' }}>
        {column.label}
      </Box>
      {onTogglePin && (
        <Box
          component="button"
          type="button"
          onClick={onTogglePin}
          aria-pressed={pinned}
          aria-label={pinned ? `Unpin ${column.label}` : `Pin ${column.label}`}
          title={pinned ? 'Unpin' : 'Pin to the left'}
          sx={{
            display: 'inline-flex',
            padding: '4px',
            border: 'none',
            borderRadius: '4px',
            background: 'transparent',
            cursor: 'pointer',
            color: pinned ? UI_TOKENS.sage700 : UI_TOKENS.grey400,
            '&:hover': { color: pinned ? UI_TOKENS.sage700 : UI_TOKENS.textSecondary },
          }}
        >
          <PhPushPinSvg size={13} />
        </Box>
      )}
      <Box
        component="button"
        type="button"
        role="switch"
        aria-checked={shown}
        aria-label={column.label}
        disabled={locked}
        onClick={onToggle}
        sx={{
          display: 'inline-flex',
          padding: 0,
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          '&:disabled': { cursor: 'default', opacity: 0.6 },
        }}
      >
        <Toggle on={shown} />
      </Box>
    </Box>
  );
};

const ColumnsMenu: FC<iColumnsMenuProps> = ({
  columns,
  hiddenColumns,
  onToggleColumn,
  columnOrder,
  onColumnOrderChange,
  pinnedColumns = [],
  onTogglePin,
}) => {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
  const keys = orderColumnKeys(
    columnOrder,
    columns.map((column) => column.key)
  );
  const byKey = Object.fromEntries(columns.map((column) => [column.key, column]));
  const shownCount = columns.length - hiddenColumns.length;

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id || !onColumnOrderChange) return;
    onColumnOrderChange(arrayMove(keys, keys.indexOf(String(active.id)), keys.indexOf(String(over.id))));
  };

  const known = new Set(keys);
  const hiddenKnown = hiddenColumns.filter((key) => known.has(key));
  const showAll = () => hiddenKnown.forEach((key) => onToggleColumn(key));
  const reset = () => {
    showAll();
    pinnedColumns.filter((key) => known.has(key)).forEach((key) => onTogglePin?.(key));
    onColumnOrderChange?.(columns.map((column) => column.key));
  };
  const footerButtonSx = {
    border: 'none',
    background: 'transparent',
    padding: 0,
    fontFamily: UI_TOKENS.fontSans,
    fontSize: 13,
    cursor: 'pointer',
  };

  return (
    <Box sx={{ width: 280, fontFamily: UI_TOKENS.fontSans }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '8px', padding: '14px 18px 2px' }}>
        <Box component="span" sx={{ fontSize: 14, fontWeight: 500, color: UI_TOKENS.textStrong }}>
          Columns
        </Box>
        <Box component="span" sx={{ fontSize: 12.5, color: UI_TOKENS.textMuted }}>
          {keys.length - hiddenKnown.length} of {keys.length} shown
        </Box>
      </Box>
      {(onColumnOrderChange || onTogglePin) && (
        <Box sx={{ padding: '2px 18px 8px', fontSize: 12, color: UI_TOKENS.textMuted }}>
          {[onColumnOrderChange ? 'Drag to reorder' : '', onTogglePin ? 'pin to keep at the start' : ''].filter(Boolean).join(' · ')}
        </Box>
      )}
      <Box sx={{ borderTop: `1px solid ${UI_TOKENS.grey50}`, padding: '4px 6px' }}>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={keys} strategy={verticalListSortingStrategy}>
            {keys.map((key) => {
              const shown = !hiddenColumns.includes(key);
              return (
                <ColumnRow
                  key={key}
                  column={byKey[key]}
                  shown={shown}
                  locked={shown && shownCount === 1}
                  pinned={pinnedColumns.includes(key)}
                  draggable={Boolean(onColumnOrderChange)}
                  onToggle={() => onToggleColumn(key)}
                  onTogglePin={onTogglePin ? () => onTogglePin(key) : undefined}
                />
              );
            })}
          </SortableContext>
        </DndContext>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: 44,
          padding: '0 18px',
          borderTop: `1px solid ${UI_TOKENS.divider}`,
        }}
      >
        <Box component="button" type="button" onClick={showAll} sx={{ ...footerButtonSx, fontWeight: 500, color: UI_TOKENS.sage600 }}>
          Show all
        </Box>
        <Box component="button" type="button" onClick={reset} sx={{ ...footerButtonSx, color: UI_TOKENS.textSecondary }}>
          Reset to default
        </Box>
      </Box>
    </Box>
  );
};

export default ColumnsMenu;
